import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { useAuth } from '@isocel/front-common';
import moment from 'moment';

import { PackageInventoryItem, StoreInventory, StoreUser } from '../../queries/api/types';
import { getStoreInventories } from '../../queries/api/store-inventory';
import { useStores } from '../../queries/store';
import { usePackagingConfig } from '../../queries/package';
import { fetchPackages } from '../../queries/api/package';

export interface GroupedInventory {
    key: string;
    date: string;
    rawDate: string;
    storeId: string;
    storeName: string;
    storeUserName: string;
    packagesInventory: PackageInventoryItem[];
}

export const useGroupedInventories = (groupBy: 'day' | 'submission' = 'day') => {
    const { user } = useAuth();
    const storeId = (user as StoreUser)?.store;
    const organizationId = user?.organization?.id;

    const [inventories, setInventories] = useState<StoreInventory[]>([]);
    const [loading, setLoading] = useState(false);

    const { data: storesData } = useStores();
    const { data: packagingConfig } = usePackagingConfig(organizationId);
    const pkgList = useMemo(() => packagingConfig?.packages ?? [], [packagingConfig]);

    const storeNameById = useMemo(() => {
        const map = new Map<string, string>();
        (storesData ?? []).forEach((s) => map.set(s.id || (s as any)._id, s.name));
        return map;
    }, [storesData]);

    const packageLabelById = useMemo(() => {
        const map = new Map<string, string>();
        pkgList.forEach((pkg: any) => {
            const label = [pkg.category?.name, pkg.type?.name, pkg.model?.name].filter(Boolean).join(' - ');
            map.set(pkg.id, label);
            if (pkg._id) map.set(pkg._id, label);
        });
        return map;
    }, [pkgList]);

    const [packageCodeById, setPackageCodeById] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        if (!organizationId) return;
        fetchPackages().then((packages) => {
            const map = new Map<string, string>();
            packages.forEach((pkg: any) => {
                const orgEntry = (pkg.organization || []).find((o: any) => o.id === organizationId);
                const code = orgEntry?.code || '';
                if (pkg.id) map.set(pkg.id, code);
                if (pkg._id) map.set(pkg._id, code);
            });
            setPackageCodeById(map);
        });
    }, [organizationId]);

    const fetchInventories = useCallback(async () => {
        if (!organizationId) return;
        setLoading(true);
        try {
            const data = await getStoreInventories({
                organizationId,
                storeId: storeId || undefined,
            });
            setInventories(data);
        } catch {
            message.error('Erreur lors du chargement des inventaires.');
        } finally {
            setLoading(false);
        }
    }, [organizationId, storeId]);

    useEffect(() => {
        fetchInventories();
    }, [fetchInventories]);

    // Group inventories by same day + same store
    const groupedInventories = useMemo((): GroupedInventory[] => {
        const map = new Map<string, GroupedInventory>();

        inventories.forEach((inv) => {
            const dateOnly = inv.createdAt ? new Date(inv.createdAt).toISOString().split('T')[0] : '';
            const groupKey = groupBy === 'submission' ? inv._id : `${inv.storeId}_${dateOnly}`;
            const stampedItems = (inv.packagesInventory ?? []).map((item) => ({
                ...item,
                inventoryDate: inv.createdAt,
                inventoryId: inv._id,
            }));
            const existing = map.get(groupKey);
            if (existing) {
                existing.packagesInventory.push(...stampedItems);
                if (inv.createdAt && inv.createdAt > existing.rawDate) {
                    existing.rawDate = inv.createdAt;
                    existing.date = moment(inv.createdAt).format('DD/MM/YYYY  HH:mm');
                }
            } else {
                map.set(groupKey, {
                    key: groupKey,
                    date: inv.createdAt ? moment(inv.createdAt).format('DD/MM/YYYY  HH:mm') : dateOnly,
                    rawDate: inv.createdAt || dateOnly,
                    storeId: inv.storeId,
                    storeName: inv.storeName || storeNameById.get(inv.storeId) || inv.storeId,
                    storeUserName: inv.storeUserName || inv.storeUserId,
                    packagesInventory: [...stampedItems],
                });
            }
        });

        return Array.from(map.values()).sort((a, b) => new Date(b.rawDate).getTime() - new Date(a.rawDate).getTime());
    }, [inventories, storeNameById, groupBy]);

    return {
        storeId,
        loading,
        groupedInventories,
        packageLabelById,
        packageCodeById,
        refetch: fetchInventories,
    };
};
